import { isSuperAdminUser } from "./roleUtils";

// Feature + coarse-action capability model.
//
// The backend (CapabilitiesService) hands the frontend, per feature group,
// `{ hasAccess, actions: [...] }` where `actions` is the distinct set of
// permission `action` values the user holds in that group (read/create/update/
// delete/execute/manage). We bucket those raw actions into three capabilities —
// read / write / delete — so UI code never has to know individual permission
// NAMES (which silently drift). This is the single source of truth for that
// bucketing; both gating (can()) and the Access Management grant toggles use it.

export const CAPABILITIES = ["read", "write", "delete"];

// capability -> the permission `action` values that satisfy it.
export const ACTION_BUCKETS = {
  read: ["read"],
  write: ["create", "update"],
  delete: ["delete"],
};

// Known feature-group keys the UI gates on. MUST match `feature_groups.key`
// in MySQL. If a group is re-keyed or its permissions left unassigned, the
// matching UI simply won't light up — check the Access Management > Feature
// Groups tab.
export const FEATURE_KEYS = {
  FILE_MANAGER: "file_manager",
  KNOWLEDGE_BASE: "knowledge_base",
  PASSWORD_VAULT: "password_vault",
  VENDOR_PURCHASE_PROGRAM: "vendor_purchase_program",
};

const normalizeAction = (value) => String(value || "").trim().toLowerCase();

// Which capability bucket a raw permission action falls into (or null).
export const bucketForAction = (action) => {
  const a = normalizeAction(action);
  return CAPABILITIES.find((cap) => ACTION_BUCKETS[cap].includes(a)) || null;
};

const grantedActionsFor = (subject, featureKey) => {
  const fg = (subject?.featureGroups || {})[featureKey];
  if (!fg || !fg.hasAccess) return null;
  return Array.isArray(fg.actions) ? fg.actions.map(normalizeAction) : [];
};

// Does `subject` have `capability` (read|write|delete) in `featureKey`?
// Super Admin short-circuits to true, mirroring hasFeatureGroup/hasPermission.
export const can = (subject, featureKey, capability) => {
  if (!subject || !featureKey) return false;
  if (isSuperAdminUser(subject)) return true;

  const granted = grantedActionsFor(subject, featureKey);
  if (granted === null) return false;

  const wanted = ACTION_BUCKETS[capability] || [];
  return wanted.some((action) => granted.includes(action));
};
